import { Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setSubLoading } from "../../Redux/Services/AuthSlice";
import { NotificationSound } from "../CommonComponents/CommonNotification";
import { ApiCall, DropDownData, convertToNumber, deleteData, handleDateChange, sanitizeInput } from "../CommonComponents/CustomHooks";
import { initializeFilterData } from "../CommonComponents/EnvConfig";
import { handleDownloadExcel } from "../CommonComponents/ExportData";
import { AutoCompleteBoxNew, DateBox, FormButton, InputBox, SelectInputBox, Switch } from "../CommonComponents/InputBox";
import { Table } from "../CommonComponents/Table";

const UserMasters = () => {
    const dispatch = useDispatch();
    const inputrefs = useRef([])
    const [filterData, setFilterData] = useState(initializeFilterData)
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([])
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [editTime, setEditTime] = useState(false)
    const initialFormData = {
        userCode: "System Generated",
        userName: "",
        address: "",
        mobileNo: "",
        emailID: "",
        password: "",
        groupName: "",
        city: "",
        state: "",
        branch: "",
        pinCode: "",
        dateofJoining: "",
        employeeCode:"",
        active: true
    }

    const [formData, setFormData] = useState(initialFormData);

    const getUserMasterData = async (CurrentPage) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        let data = {
            ...filterData,
            ...sortConfig,
            "PageIndex": PageIndex.toString(),
            "Type": "S",
        }
        setIsLoading(true)
        let res = await ApiCall(`/api/User/ShowUserMaster`, data)
        if (res.Status === "SUCCESS") {
            setTableData(res)
        }
        if (res.Status === "ERROR") {
            setTableData([])
        }
        setIsLoading(false)
        if (res.Status === 'EXPIRED' || res.Status === 'UNAUTHORIZED') {
            await NotificationSound(res.Status, res.Message, res.focus, inputrefs);
        }
    }

    const handleSubmit = async () => {
        await dispatch(setSubLoading(true))
        let data = await ApiCall(`/api/User/InsertUpdateUserMaster`, { ...formData, IsImport: false })
        await dispatch(setSubLoading(false))
        await NotificationSound(data.status, data.message, data.focus, inputrefs, handleClear);
    };

    const GetCityStateByPincode = async (dataPayload) => {
        await dispatch(setSubLoading(true));
        let data = await ApiCall(`/api/Master/GetCityStateByPincode`, dataPayload);
        await dispatch(setSubLoading(false));
        if (data?.city || data?.state) {
            setFormData(prev => ({
                ...prev,
                city: data?.city || "",
                state: data?.state || ""
            }));
        }
    };

    const handleEditData = async (id) => {
        await dispatch(setSubLoading(true))
        let editData = await ApiCall(`/api/User/EditUserMaster`, { UserID: id })
        await dispatch(setSubLoading(false))
        if (editData.status === 'SUCCESS') {
            setEditTime(true)
            await inputrefs.current[editData.focus].focus();
            setFormData((pre) => ({
                ...pre, ...editData,
            }))
        } else {
            await NotificationSound(editData.status, editData.message, editData.focus, inputrefs);
        }
    }

    const handleDelete = async (id) => {
        await dispatch(setSubLoading(true))
        await deleteData(`/api/User/DeleteUserMaster`, { UserID: id }, getUserMasterData, handleClear, '', inputrefs)
        await dispatch(setSubLoading(false))
    }

    const handleChange1 = (i, p) => {
        getUserMasterData(p)
    };

    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig && sortConfig.SortColumn === SortColumn && sortConfig.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        setSortConfig({ SortColumn, SortOrder });
    };

    const GetGroupName = DropDownData('GetUserGroupName')

    const handleClear = () => {
        setFormData(initialFormData)
        getUserMasterData()
        setEditTime(false)
    }

    useEffect(() => {
        document.title = `User Master`
        inputrefs.current['txtUserName'].focus()
    }, [])

    useEffect(() => {
        getUserMasterData();
    }, [sortConfig]);

    return (
        <>
            <div className='bg-secondary-50 shadow-lg rounded-md p-3'>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary-600" />
                        <h2 className="text-xl font-semibold text-primary-600">User Master</h2>
                    </div>
                </div>
                <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
                    <InputBox
                        id="txtUserCode"
                        label='User Code'
                        inputrefs={inputrefs}
                        placeholder='Enter User Code'
                        name='UserCode'
                        value={formData.userCode}
                        disabled={true}
                    />
                    <InputBox
                        id="txtUserName"
                        type="text"
                        label='User Name'
                        required={true}
                        placeholder="Enter User Name"
                        maxLength='50'
                        value={formData.userName}
                        onChange={(e) => setFormData({ ...formData, userName: sanitizeInput(e.target.value) })}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { inputrefs.current['txtEmployeeCode'].focus() };
                        }}
                    />
                    <InputBox
                        id="txtEmployeeCode"
                        type="text"
                        label='Employee Code'
                        disabled={editTime}
                        placeholder="Enter Employee Code"
                        maxLength='50'
                        value={formData.employeeCode}
                        onChange={(e) => setFormData({ ...formData, employeeCode: sanitizeInput(e.target.value) })}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { inputrefs.current['txtGroupName'].focus() };
                        }}
                    />

                    <SelectInputBox
                        id="txtGroupName"
                        value={formData.groupName}
                        label='Group Name'
                        required={true}
                        onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['txtBranch'].focus()
                            }
                        }}
                    >
                        <option value=''>--Please Select--</option>
                        {GetGroupName?.data?.dropDownData?.map((item, index) =>
                            <option key={item.textPart + index} value={item.valuePart}>{item.textPart}</option>
                        )}
                    </SelectInputBox>

                    <AutoCompleteBoxNew
                        id="txtBranch"
                        required={true}
                        placeholder="Enter Branch"
                        label='Branch'
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { inputrefs.current["txtPassword"].focus(); };
                        }}
                        autocompleteType='GetBranch'
                        value={formData.branch || ''}
                        onChange={async (event, newInputValue) => {
                            newInputValue = newInputValue || ''
                            if (event.type !== 'keydown') {
                                setFormData({ ...formData, branch: newInputValue })
                            }
                        }}
                    />

                    <InputBox
                        id="txtPassword"
                        placeholder="Enter Password"
                        label='Password'
                        type='password'
                        required={true}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['txtMobile'].focus()
                            }
                        }}
                    />

                    <InputBox
                        id="txtMobile"
                        placeholder="Enter Mobile No"
                        label='Mobile No'
                        maxLength='10'
                        value={formData.mobileNo}
                        onChange={(e) => setFormData({ ...formData, mobileNo: convertToNumber(e.target.value) })}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['txtEmailID'].focus()
                            }
                        }}
                    />
                    <InputBox
                        id="txtEmailID"
                        type="email"
                        placeholder="Enter Email ID"
                        autoComplete="off"
                        maxLength='50'
                        label='Email ID'
                        value={formData.emailID}
                        onChange={(e) => setFormData({ ...formData, emailID: (e.target.value) })}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['txtAddress'].focus()
                            }
                        }}
                    />

                    <InputBox
                        id="txtAddress"
                        placeholder="Enter Address"
                        maxLength='100'
                        label='Address'
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['txtPinCode'].focus()
                            }
                        }}
                    />
                    <AutoCompleteBoxNew
                        id="txtPinCode"
                        placeholder="Enter PinCode"
                        label='PinCode'
                        required={true}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { inputrefs.current["txtCity"].focus(); };
                        }}
                        autocompleteType='GetPinCode'
                        value={formData.pinCode || ''}
                        onChange={async (event, newInputValue) => {
                            newInputValue = newInputValue || '';

                            if (event.type !== 'keydown') {
                                const updatedData = { ...formData, pinCode: newInputValue };
                                setFormData(updatedData);
                                GetCityStateByPincode(updatedData);
                            }
                        }}
                    />
                    <AutoCompleteBoxNew
                        id="txtCity"
                        placeholder="Enter City"
                        label='City'
                        required={true}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { inputrefs.current["txtState"].focus(); };
                        }}
                        autocompleteType='GetCity'
                        contextKey={formData.pinCode}
                        value={formData.city || ''}
                        onChange={async (event, newInputValue) => {
                            newInputValue = newInputValue || ''
                            if (event.type !== 'keydown') {
                                setFormData({ ...formData, city: newInputValue })
                            }
                        }}
                    />
                    <AutoCompleteBoxNew
                        id="txtState"
                        placeholder="Enter State"
                        label='State'
                        required={true}
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { inputrefs.current["txtDateOfJoining"].focus(); };
                        }}
                        autocompleteType='GetState'
                        contextKey={formData.pinCode}
                        contextKey1={formData.city}
                        value={formData.state || ''}
                        onChange={async (event, newInputValue) => {
                            newInputValue = newInputValue || ''
                            if (event.type !== 'keydown') {
                                setFormData({ ...formData, state: newInputValue })
                            }
                        }}
                    />

                    <DateBox
                        inputrefs={inputrefs}
                        label="Date of Joining"
                        placeholder="DD-MMM-YYYY"
                        id='txtDateOfJoining'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['txtActive'].focus()
                            };
                        }}
                        selected={formData.dateofJoining === '' ? '' : new Date(formData.dateofJoining)}
                        onChange={(e) => handleDateChange(e, setFormData, formData, 'dateofJoining')}
                    />

                    <Switch
                        size='lg'
                        inputrefs={inputrefs}
                        label='Active'
                        checked={formData.active}
                        id='txtActive'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                inputrefs.current['btnSub'].focus()
                            };
                        }}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                </div>

                <div className='card-body'>
                    <FormButton
                        id={'btnSub'}
                        inputrefs={inputrefs}
                        handleSubmit={handleSubmit}
                        handleExport={async () => {
                            await dispatch(setSubLoading(true))
                            handleDownloadExcel(`/api/User/ShowUserMaster`, filterData, 'User Master')
                            await dispatch(setSubLoading(false))
                        }}
                        handleClear={handleClear}
                    />
                </div>

            </div>

            <div className="mt-3">
                <Table
                    handleEdit={handleEditData}
                    handleDelete={handleDelete}
                    loading={isLoading}
                    PageName={'UserMaster'}
                    handleFilter={() => {
                        getUserMasterData()
                        setFilterData({
                            ...filterData,
                        })
                    }}
                    filterData={filterData}
                    tableData={tableData}
                    setFilterData={setFilterData}
                    currentPageNo={currentPageNo}
                    handleChange1={handleChange1}
                    sortConfig={sortConfig}
                    handleSorting={handleSorting}
                />
            </div>
        </>
    )
}
export default UserMasters;